import { ButtonPrimary, ButtonText } from 'components/Button'
import { EmptyState } from 'components/EmptyState'
import { LoadingRows } from 'components/Loader/styled'
import PositionList from 'components/PositionList'
import { PositionListFooter } from 'components/PositionList/styled'
import { POSITIONS_PER_PAGE, useV3Positions } from 'hooks/useV3Positions'
import { RefreshControl } from 'pages/Liquidity'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AlertTriangle } from 'react-feather'
import { useUserHideClosedPositions } from 'state/user/hooks'
import styled, { css, ThemeContext } from 'styled-components'
import { StyledInternalLink, ThemedText } from 'theme'

export const MainContentWrapper = styled.main`
  background-color: ${({ theme }) => theme.cardPrimary};
  /* padding: 16px; */
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.09);
  width: 100%;
  position: relative;
`

const Wrapper = styled.div`
  width: 100%;
`

const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  padding: 24px 0;
  max-width: 300px;
  /* min-height: 25vh; */
`

const IconStyle = css`
  width: 40px;
  height: 40px;
  margin-bottom: 0.5rem;
`

export const ResponsiveButtonPrimary = styled(ButtonPrimary)`
  padding: 6px 16px;
  border: none;
  width: fit-content;

  background-color: transparent;

  border-radius: 4px;
  border: 1px solid #64748b;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex: 1 1 auto;
    width: 100%;
  `};

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  &:hover {
    background: ${({ theme }) => theme.stateOverlayHover};
  }
`
const NetworkIcon = styled(AlertTriangle)`
  ${IconStyle}
`

const DataTransitionOverlay = styled.div`
  position: absolute;
  top: 58px; /* Leave space for header like Islands page */
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.cardPrimary};
  z-index: 200; /* Higher z-index like PositionList */
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  border-radius: 16px;
  animation: fadeIn 200ms ease-in; /* Match PositionList timing */

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1; /* Full opacity like PositionList */
    }
  }
`

function PositionsLoadingPlaceholder() {
  return (
    <LoadingRows>
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
    </LoadingRows>
  )
}

export default function PoolV3({
  account,
  chainId,
  toggleWalletModal,
  searchParam,
  refreshControl,
}: {
  account?: string | null
  chainId?: number | null
  toggleWalletModal?: () => void
  searchParam?: string | null
  refreshControl?: RefreshControl
}) {
  const theme = useContext(ThemeContext)
  const [userHideClosedPositions, setUserHideClosedPositions] = useUserHideClosedPositions()
  const [hasSettledOnce, setHasSettledOnce] = useState(false)

  const {
    positions,
    openPositions,
    closedPositions,
    loading: positionsLoading,
    totalCount,
    currentPage,
    totalPages,
    goToPage,
    isSearching,
    isError,
    error,
    refetch,
  } = useV3Positions(
    account,
    userHideClosedPositions,
    {
      pageSize: POSITIONS_PER_PAGE,
      enablePagination: true,
    },
    searchParam || undefined
  )

  useEffect(() => {
    if (refreshControl?.onRegister && refetch) {
      const asyncRefetch = async () => {
        refetch()
      }
      refreshControl.onRegister(asyncRefetch)
    }

    return () => {
      if (refreshControl?.onRegister) {
        refreshControl.onRegister(null)
      }
    }
  }, [refreshControl?.onRegister, refetch])

  const prevLoading = useRef(positionsLoading)

  useEffect(() => {
    if (prevLoading.current && !positionsLoading) {
      setHasSettledOnce(true)
    }
    prevLoading.current = positionsLoading
  }, [positionsLoading])

  const hasData = positions.length > 0
  const fatalError = isError && !positionsLoading && !hasData
  const unsupportedChain = fatalError && error?.name === 'UnsupportedChainId'

  const showConnectAWallet = Boolean(!account)
  const isConnected = Boolean(account)

  const showTransitionOverlay = refreshControl?.isRefreshing || isSearching || (positionsLoading && !hasSettledOnce)
  const showList = isConnected && (positionsLoading || hasData) && !showTransitionOverlay
  const showEmptyShell = (!isConnected || (!positionsLoading && !hasData)) && !showTransitionOverlay

  const handlePageChange = useCallback(
    (page: number) => {
      goToPage(page - 1)
    },
    [goToPage]
  )

  const getTransitionLabel = () => {
    if (refreshControl?.isRefreshing) {
      return 'Refreshing positions...'
    }
    if (isSearching) {
      if (searchParam) {
        const isIdLike = /^\d+$/.test(searchParam.trim())
        if (isIdLike) {
          return `Searching for position "${searchParam}"...`
        }
        return `Searching positions for "${searchParam}"...`
      }
      return 'Searching positions...'
    }
    if (positionsLoading && !hasSettledOnce) {
      return 'Loading positions...'
    }
    return 'Loading...'
  }

  return (
    <>
      <Wrapper>
        {(showList || showTransitionOverlay) && !fatalError && (
          <PositionList
            positions={positions}
            openPositions={openPositions}
            closedPositions={closedPositions}
            setUserHideClosedPositions={setUserHideClosedPositions}
            userHideClosedPositions={userHideClosedPositions}
            loading={false}
            totalCount={totalCount}
            currentPage={currentPage + 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            searchParam={searchParam}
            isSearching={false}
            showTransitionOverlay={showTransitionOverlay}
            transitionLabel={getTransitionLabel()}
          />
        )}

        {fatalError && !showTransitionOverlay && (
          <MainContentWrapper>
            <ErrorContainer>
              <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                <NetworkIcon />
                <div style={{ marginTop: 8 }}>
                  {unsupportedChain
                    ? 'Your connected network is unsupported.'
                    : error?.message || 'Something went wrong while loading your positions.'}
                </div>
              </ThemedText.DeprecatedBody>

              {!unsupportedChain && (
                <ResponsiveButtonPrimary style={{ marginTop: '1rem' }} onClick={() => refetch()}>
                  Try again
                </ResponsiveButtonPrimary>
              )}
            </ErrorContainer>
          </MainContentWrapper>
        )}

        {showEmptyShell && (
          <MainContentWrapper>
            <ErrorContainer>
              <EmptyState
                message={
                  showConnectAWallet
                    ? 'Connect your wallet to see your v3 positions.'
                    : searchParam && searchParam.length === 42
                    ? `No positions found for token ${searchParam.slice(0, 6)}...${searchParam.slice(-4)}`
                    : 'Your active V3 liquidity positions will appear here.'
                }
                showConnectButton={showConnectAWallet}
              />

              {!showConnectAWallet && !userHideClosedPositions && (
                <ButtonText
                  style={{ marginTop: '.5rem' }}
                  onClick={() => setUserHideClosedPositions(!userHideClosedPositions)}
                >
                  Show closed positions
                </ButtonText>
              )}
            </ErrorContainer>

            <PositionListFooter>
              <ResponsiveButtonPrimary
                as={StyledInternalLink}
                to="/liquidity/v3/add"
                id="join-pool-button"
                data-cy="join-pool-button"
              >
                New Position
              </ResponsiveButtonPrimary>
            </PositionListFooter>
          </MainContentWrapper>
        )}
      </Wrapper>
    </>
  )
}
